import { supabase } from '../lib/supabase';
import { Staff, DashboardStats, MenuItem, Review } from '../types';

// --- Auth ---

export const adminLogin = async (email: string): Promise<Staff | null> => {
  const normalizedEmail = email.toLowerCase().trim();

  try {
    const { data } = await supabase
      .from('staff')
      .select('*')
      .eq('email', normalizedEmail)
      .eq('is_active', true)
      .maybeSingle();

    if (data) return data as Staff;
  } catch (err) {
    console.warn('Supabase login check failed', err);
  }
  return null;
};

// --- Dashboard ---

export const getDashboardStats = async (): Promise<DashboardStats> => {
  // Use local midnight converted to UTC to ensure we catch all orders from the start of the user's day
  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);
  const todayISO = startOfDay.toISOString();

  try {
    // 1. Revenue (Sum of today's orders)
    const { data: todayOrders } = await supabase
      .from('orders')
      .select('total_amount')
      .gte('created_at', todayISO)
      .neq('status', 'cancelled');

    const todayRevenue = todayOrders?.reduce((sum, o) => sum + (Number(o.total_amount) || 0), 0) || 0;

    // 2. Total Revenue (All Time)
    const { data: allOrders } = await supabase
      .from('orders')
      .select('total_amount')
      .neq('status', 'cancelled');

    const totalRevenue = allOrders?.reduce((sum, o) => sum + (Number(o.total_amount) || 0), 0) || 0;

    // 3. Active Orders
    const { count: activeOrders } = await supabase
      .from('orders')
      .select('*', { count: 'exact', head: true })
      .in('status', ['pending', 'preparing']);

    // 4. Today's Reservations
    // For reservations, they are stored as 'YYYY-MM-DD' usually, so we compare DATE types if possible
    // Or if accessing timestamp, use same logic.
    // Schema says: reservation_date DATE NOT NULL
    // So we need local YYYY-MM-DD string
    const localDateString = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format in CA locale

    const { count: todayReservations } = await supabase
      .from('reservations')
      .select('*', { count: 'exact', head: true })
      .eq('reservation_date', localDateString)
      .neq('status', 'cancelled');

    // 5. Pending Reviews
    const { count: pendingReviews } = await supabase
      .from('reviews')
      .select('*', { count: 'exact', head: true })
      .eq('is_approved', false);

    return {
      todayRevenue,
      totalRevenue,
      activeOrders: activeOrders || 0,
      todayReservations: todayReservations || 0,
      pendingReviews: pendingReviews || 0
    };
  } catch (e) {
    console.error("Error fetching stats:", e);
    return { todayRevenue: 0, totalRevenue: 0, activeOrders: 0, todayReservations: 0, pendingReviews: 0 };
  }
};

// --- Revenue Trend ---

export const getRevenueTrend = async () => {
  const labels: string[] = [];
  const trend: number[] = [];
  const dateMap: Record<string, number> = {};

  // 1. Generate last 7 days labels and map keys
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const dateKey = d.toLocaleDateString('en-CA'); // YYYY-MM-DD
    const label = d.toLocaleDateString('en-US', { weekday: 'short' }); // Mon, Tue...

    labels.push(label);
    trend.push(0);
    dateMap[dateKey] = 6 - i; // Map date string to index (0-6)
  }

  // 2. Fetch data
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const startDate = sevenDaysAgo.toISOString();

  const { data: orders } = await supabase
    .from('orders')
    .select('created_at, total_amount')
    .gte('created_at', startDate)
    .neq('status', 'cancelled');

  // 3. Aggregate data
  if (orders) {
    orders.forEach(order => {
      // Convert UTC DB time to Local Date Key
      const orderDate = new Date(order.created_at).toLocaleDateString('en-CA');

      const index = dateMap[orderDate];
      if (index !== undefined) {
        trend[index] += Number(order.total_amount) || 0;
      }
    });
  }

  return { data: trend, labels };
};

// --- Orders ---

export const getAdminOrders = async () => {
  const { data, error } = await supabase
    .from('orders')
    .select(`
      *,
      customers (
        full_name,
        phone
      )
    `)
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) {
    console.error('Error fetching admin orders:', error);
    return [];
  }
  return data;
};

export const updateOrderStatus = async (orderId: string, status: string, staffId: string) => {
  const { error } = await supabase
    .from('orders')
    .update({ status })
    .eq('id', orderId);

  if (error) throw error;
};

// --- Reservations ---

export const getAdminReservations = async () => {
  const today = new Date().toISOString().split('T')[0];
  const { data, error } = await supabase
    .from('reservations')
    .select(`
      *,
      customers (
        full_name,
        phone
      )
    `)
    .gte('reservation_date', today)
    .order('reservation_date', { ascending: true })
    .order('reservation_time', { ascending: true });

  if (error) {
    console.error('Error fetching admin reservations:', error);
    return [];
  }
  return data;
};

export const updateReservationStatus = async (resId: string, status: string, staffId: string) => {
  const { error } = await supabase
    .from('reservations')
    .update({ status })
    .eq('id', resId);

  if (error) throw error;
};

// --- Menu ---

export const getAllMenuItems = async () => {
  const { data, error } = await supabase
    .from('menu_items')
    .select('*')
    .order('category');

  if (error) throw error;

  return (data || []).map(item => ({
    ...item,
    image: item.image_url || item.image
  })) as MenuItem[];
};

export const updateMenuItem = async (id: string, updates: Partial<MenuItem>) => {
  const dbUpdates = { ...updates };
  // @ts-ignore
  if (dbUpdates.image) {
    // @ts-ignore
    dbUpdates.image_url = dbUpdates.image;
    // @ts-ignore
    delete dbUpdates.image;
  }

  const { error } = await supabase
    .from('menu_items')
    .update(dbUpdates)
    .eq('id', id);

  if (error) throw error;
};

export const createMenuItem = async (item: Omit<MenuItem, 'id'>) => {
  const dbItem = { ...item };
  // @ts-ignore
  if (dbItem.image) {
    // @ts-ignore
    dbItem.image_url = dbItem.image;
    // @ts-ignore
    delete dbItem.image;
  }

  const { error } = await supabase
    .from('menu_items')
    .insert([dbItem]);

  if (error) throw error;
};

export const deleteMenuItem = async (id: string) => {
  const { error } = await supabase
    .from('menu_items')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

// --- Reviews ---

export const getAllReviews = async () => {
  const { data, error } = await supabase
    .from('reviews')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as Review[];
};

export const moderateReview = async (id: string, is_approved: boolean) => {
  const { error } = await supabase
    .from('reviews')
    .update({ is_approved })
    .eq('id', id);

  if (error) throw error;
};

// --- Staff ---

export const getAllStaff = async () => {
  const { data, error } = await supabase
    .from('staff')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as Staff[];
};

export const createStaff = async (staff: Omit<Staff, 'id' | 'created_at' | 'last_login'>) => {
  const { error } = await supabase
    .from('staff')
    .insert([staff]);

  if (error) throw error;
};

export const updateStaff = async (id: string, updates: Partial<Staff>) => {
  const { error } = await supabase
    .from('staff')
    .update(updates)
    .eq('id', id);

  if (error) throw error;
};

export const deleteStaff = async (id: string) => {
  const { error } = await supabase
    .from('staff')
    .delete()
    .eq('id', id);

  if (error) throw error;
};